/**
 * Visual rule builder — конвертация размеченного сообщения в regex.
 *
 * Идея: оператор-непрограммист берёт пример сообщения, выделяет в нём
 * кусочки и помечает их типами («это сумма», «это валюта», «это ID
 * заявки»). Этот модуль превращает разметку в обычный regex с named
 * groups, который потом сохраняется в БД и используется в matching.
 *
 * Пример:
 *   сегменты: [literal "Получено "] [amount "amount"] [literal " "]
 *             [ccy "ccy"] [literal " по заявке "] [orderId "orderId"]
 *   → pattern: ^Получено (?<amount>[\d\s.,]+) (?<ccy>[A-Z]{3,4}) по заявке (?<orderId>W-\d+)$
 *   → flags: im
 */

import { KNOWN_CCY_SYMBOLS } from './currencies.js';

/**
 * Типы переменных, которые оператор может разметить в примере сообщения.
 * Каждый тип имеет regex-паттерн (см. VARIABLE_TYPE_PATTERN), цвет/группу
 * для UI (см. VARIABLE_TYPE_GROUP), русское название (VARIABLE_TYPE_LABELS).
 *
 * v2 (10 новых): date, time, phone, email, tgUsername, cardLast4, iban,
 * accountNumber, url, name.
 * v3 (9 новых): percent, signedAmount, anyLine, ccySymbol, cryptoAddress,
 * txHash, swiftBic, cardNumber, flagEmoji.
 */
export const VARIABLE_TYPES = [
  // ─── Деньги/идентификаторы (core) ───
  'amount',
  'signedAmount',
  'percent',
  'ccy',
  'ccySymbol',
  'orderId',
  'idx',
  // ─── Контакты ───
  'phone',
  'email',
  'tgUsername',
  'name',
  // ─── Реквизиты ───
  'cardLast4',
  'cardNumber',
  'iban',
  'accountNumber',
  'swiftBic',
  // ─── Время ───
  'date',
  'time',
  // ─── Крипто ───
  'cryptoAddress',
  'txHash',
  // ─── Прочее ───
  'url',
  'anyNumber',
  'anyText',
  'anyLine',
  'flagEmoji',
] as const;
export type VariableType = (typeof VARIABLE_TYPES)[number];

/** Человекочитаемое имя типа для UI (русский). */
export const VARIABLE_TYPE_LABELS: Record<VariableType, string> = {
  amount: 'Сумма',
  signedAmount: 'Сумма со знаком',
  percent: 'Процент',
  ccy: 'Валюта',
  ccySymbol: 'Символ валюты',
  orderId: 'ID заявки',
  idx: 'Номер проводки',
  phone: 'Телефон',
  email: 'Email',
  tgUsername: 'Telegram @username',
  name: 'Имя / ФИО',
  cardLast4: 'Карта (последние 4)',
  cardNumber: 'Номер карты',
  iban: 'IBAN',
  accountNumber: 'Номер счёта',
  swiftBic: 'SWIFT/BIC',
  date: 'Дата',
  time: 'Время',
  cryptoAddress: 'Крипто-адрес',
  txHash: 'Хэш транзакции',
  url: 'Ссылка',
  anyNumber: 'Любое число',
  anyText: 'Любой текст',
  anyLine: 'Остаток строки',
  flagEmoji: 'Эмодзи-флаг',
};

/**
 * Группа для UI — кнопки toolbar и chips группируются по смыслу.
 * Группа задаёт цветовой токен (см. ниже VARIABLE_TYPE_COLOR_GROUP).
 */
export const VARIABLE_TYPE_GROUP: Record<VariableType, 'money' | 'id' | 'contact' | 'requisite' | 'time' | 'crypto' | 'other'> = {
  amount: 'money',
  signedAmount: 'money',
  percent: 'money',
  ccy: 'id',
  ccySymbol: 'id',
  orderId: 'id',
  idx: 'id',
  phone: 'contact',
  email: 'contact',
  tgUsername: 'contact',
  name: 'contact',
  cardLast4: 'requisite',
  cardNumber: 'requisite',
  iban: 'requisite',
  accountNumber: 'requisite',
  swiftBic: 'requisite',
  date: 'time',
  time: 'time',
  cryptoAddress: 'crypto',
  txHash: 'crypto',
  url: 'other',
  anyNumber: 'other',
  anyText: 'other',
  anyLine: 'other',
  flagEmoji: 'other',
};

/** Цветовая семантика по группе (фронт мапит на CSS-vars из проекта). */
export type VariableTypeGroup = 'money' | 'id' | 'contact' | 'requisite' | 'time' | 'crypto' | 'other';

export const VARIABLE_GROUP_COLOR: Record<VariableTypeGroup, string> = {
  money: 'green',
  id: 'accent',
  contact: 'info',
  requisite: 'warning',
  time: 'info',
  crypto: 'violet',
  other: 'gray',
};

/**
 * Regex-шаблоны для named groups. Не-жадные где это критично (amount,
 * anyText). Поддержка форматов из реальных банковских/обменных сообщений:
 *
 * - `amount`: числа с разделителями (1 234,56 / 1,234.56 / 1.234,56).
 * - `ccy`: ISO-код валюты (3-4 буквы заглавных).
 * - `orderId`: формат Wave Panel W-NNN.
 * - `idx`: одна цифра 0..3 (номер проводки).
 * - `phone`: международный/национальный формат с разделителями.
 * - `email`: стандартный.
 * - `tgUsername`: @username (3-32 символа).
 * - `name`: одно или несколько слов с большой буквы (рус/eng).
 * - `cardLast4`: ****1234 / *1234 / x1234 / 1234 (последние 4 цифры).
 * - `iban`: BY00ABC... / RU00... (2 буквы + 2 цифры + до 30 символов).
 * - `accountNumber`: 11-20 цифр (РФ счёт 20 знаков, СНИЛС 11).
 * - `date`: DD.MM.YYYY / DD/MM/YYYY / DD-MM-YYYY (год 2 или 4 цифры).
 * - `time`: HH:MM или HH:MM:SS.
 * - `url`: http/https URL.
 * - `anyNumber`: целое или дробное.
 * - `anyText`: непустая последовательность не-whitespace.
 *
 * v3:
 * - `signedAmount`: тело как у amount + опциональный ведущий знак `+`/`-`/
 *   юникод-минус `−` (U+2212). Знак попадает в захват — parseMoney
 *   (rule-engine) нормализует его.
 * - `percent`: чистое число (опц. знак), сам `%` в захват НЕ попадает —
 *   он проверяется lookahead'ом `(?=\s?%)` и матчится литералом `%`,
 *   размеченным оператором после placeholder'а. Иначе parseMoney упадёт.
 * - `anyLine`: всё до конца строки (`[^\n]+` — не пересекает перевод строки).
 * - `ccySymbol`: альтернация известных символов валют (см. KNOWN_CCY_SYMBOLS
 *   в currencies.ts); многосимвольные (د.إ) стоят раньше односимвольных.
 * - `cryptoAddress`: TRON (T…) / ETH (0x…) / BTC (bech32 bc1… и legacy 1…/3…),
 *   от специфичного к общему.
 * - `txHash`: 64 hex-символа с опциональным префиксом 0x.
 * - `swiftBic`: SWIFT/BIC — 8 или 11 символов.
 * - `cardNumber`: полный номер карты 13-19 цифр с опц. пробелами/дефисами.
 * - `flagEmoji`: эмодзи-флаг = ровно пара regional indicator. Записан
 *   суррогатными парами — работает БЕЗ флага `u` (в системе он не используется).
 */

/**
 * Альтернация символов валют для типа `ccySymbol`: многосимвольные (د.إ) —
 * раньше односимвольных (сортировка по длине убыв.), каждый экранирован.
 */
const CCY_SYMBOL_ALTERNATION = `(?:${[...KNOWN_CCY_SYMBOLS]
  .sort((a, b) => b.length - a.length)
  .map(escapeRegex)
  .join('|')})`;

export const VARIABLE_TYPE_PATTERN: Record<VariableType, string> = {
  amount: '[\\d\\s.,]+?',
  signedAmount: '[+\\-−]?[\\d\\s.,]+?',
  percent: '[+\\-−]?\\d+(?:[.,]\\d+)?(?=\\s?%)',
  ccy: '[A-Z]{3,4}',
  ccySymbol: CCY_SYMBOL_ALTERNATION,
  orderId: 'W-\\d+',
  idx: '[0-3]',
  phone: '\\+?\\d[\\d\\s\\-()]{7,18}\\d',
  email: '[\\w.+\\-]+@[\\w\\-]+\\.[\\w.\\-]+',
  tgUsername: '@\\w{3,32}',
  name: "[А-ЯЁA-Z][а-яёa-zA-Z'\\-]+(?:\\s+[А-ЯЁA-Z][а-яёa-zA-Z'\\-]+){0,3}",
  cardLast4: '(?:\\*+|x+|X+)?\\d{4}',
  cardNumber: '\\d(?:[ -]?\\d){12,18}',
  iban: '[A-Z]{2}\\d{2}[A-Z0-9]{1,30}',
  accountNumber: '\\d{11,20}',
  swiftBic: '[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?',
  date: '\\d{1,2}[./\\-]\\d{1,2}[./\\-]\\d{2,4}',
  time: '\\d{1,2}:\\d{2}(?::\\d{2})?',
  cryptoAddress:
    '(?:T[1-9A-HJ-NP-Za-km-z]{33}|0x[a-fA-F0-9]{40}|bc1[a-z0-9]{25,59}|[13][1-9A-HJ-NP-Za-km-z]{25,34})',
  txHash: '(?:0x)?[a-fA-F0-9]{64}',
  url: 'https?://\\S+',
  anyNumber: '\\d+(?:[.,]\\d+)?',
  anyText: '\\S+',
  anyLine: '[^\\n]+',
  flagEmoji: '(?:\\uD83C[\\uDDE6-\\uDDFF]){2}',
};

/** Сегмент шаблона. */
export type Segment =
  | { kind: 'literal'; text: string }
  | { kind: 'placeholder'; type: VariableType; name: string };

/** Экранирование специальных regex-символов в литералах. */
export function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Построить regex из размеченных сегментов.
 *
 * Конвенции:
 * - В литералах пробелы заменяются на `\s+` — это устраняет проблемы
 *   с разной пробельной разметкой банков (один/два пробела/неразрывный).
 * - Pattern якорится `^...$` — правило срабатывает только если шаблон
 *   совпадает со всем сообщением (или со строкой при multiline flag `m`).
 * - Default flags: `im` (case-insensitive + multiline).
 * - `opts.matchAll` → flags `gim`: движок (matchAllNamed) вернёт ВСЕ
 *   совпадения в сообщении — каждая подошедшая строка мультистрочного
 *   сообщения (например, «баланс по валюте на строку») даёт отдельное
 *   событие. Pattern при этом не меняется.
 */
export function buildRegexFromSegments(
  segments: Segment[],
  opts?: { matchAll?: boolean },
): { pattern: string; flags: string } {
  const parts = segments.map((seg) => {
    if (seg.kind === 'literal') {
      // Заменяем подряд идущие whitespace на \s+ для устойчивости.
      return escapeRegex(seg.text).replace(/(?:\\?\s)+/g, '\\s+');
    }
    return `(?<${seg.name}>${VARIABLE_TYPE_PATTERN[seg.type]})`;
  });
  return { pattern: `^${parts.join('')}$`, flags: opts?.matchAll ? 'gim' : 'im' };
}

/**
 * Сериализовать сегменты в компактный JSON для хранения в БД
 * (поле в action_config). Позволяет потом открыть правило в визуальном
 * редакторе вместо raw-regex.
 */
export function serializeSegments(segments: Segment[]): string {
  return JSON.stringify(segments);
}

export function parseSegments(json: string): Segment[] | null {
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return null;
    for (const item of arr) {
      if (item.kind === 'literal' && typeof item.text === 'string') continue;
      if (
        item.kind === 'placeholder' &&
        typeof item.name === 'string' &&
        VARIABLE_TYPES.includes(item.type)
      )
        continue;
      return null;
    }
    return arr as Segment[];
  } catch {
    return null;
  }
}

/**
 * На основе типов выбранных переменных рекомендует actionType.
 * Если оператор выбрал и amount, и ccy — предложить update-rate.
 * Если orderId — предложить confirm-provodka.
 * Если есть всё для create-order — предложить (но это редкий случай).
 */
export function suggestActionType(segments: Segment[]): 'update-rate' | 'confirm-provodka' | 'create-order' | 'log-only' {
  const types = new Set(segments.filter((s) => s.kind === 'placeholder').map((s) => (s as { type: VariableType }).type));
  if (types.has('orderId')) return 'confirm-provodka';
  if (types.has('amount') && types.has('ccy')) return 'update-rate';
  if (types.size === 0) return 'log-only';
  return 'log-only';
}

/**
 * Автоматически построить actionConfig из сегментов + actionType.
 * Использует названия placeholder'ов как имена named groups.
 *
 * Для типов, которые не имеют автогенерации (send-message, reject-order
 * и т.п.) — возвращает пустой объект, оператор заполняет вручную.
 */
export function buildActionConfig(segments: Segment[], actionType: string): Record<string, unknown> {
  const byType = new Map<VariableType, string>();
  for (const seg of segments) {
    if (seg.kind === 'placeholder' && !byType.has(seg.type)) {
      byType.set(seg.type, seg.name);
    }
  }
  switch (actionType) {
    case 'update-rate':
      return {
        ccyGroup: byType.get('ccy') ?? 'ccy',
        rateGroup: byType.get('amount') ?? byType.get('anyNumber') ?? 'rate',
      };
    case 'confirm-provodka':
      return {
        orderIdGroup: byType.get('orderId') ?? 'orderId',
        ...(byType.get('idx') ? { idxGroup: byType.get('idx') } : {}),
        defaultIdx: 0,
      };
    case 'reject-order':
      return {
        orderIdGroup: byType.get('orderId') ?? 'orderId',
        reasonTemplate: 'Отклонено по правилу',
      };
    case 'edit-provodka':
      return {
        orderIdGroup: byType.get('orderId') ?? 'orderId',
        amountGroup: byType.get('amount') ?? 'amount',
        defaultIdx: 0,
      };
    default:
      // log-only / create-order / send-message / custom-webhook — пустой config.
      return {};
  }
}
